import { AmbientLight, DirectionalLight, HemisphereLight } from 'three';

import { DESIGN_PACKAGE_LOOK, DESIGN_PACKAGE_NEUTRALIZE_LIGHTS } from '../../../../constants/design-package-look';
import type { LightingManager } from '../lighting-manager.class';
import { DEFAULT_LIGHTING_PRESET, type LightingPreset } from '../types';

export interface InitializeParams {
	preset?: LightingPreset;
}

/**
 * Initialize lighting with default preset
 */
export const initializeCfn = (self: LightingManager, params: InitializeParams): void => {
	const { preset = DEFAULT_LIGHTING_PRESET } = params;
	const config = self.internalConfig;
	const { scene, shadowsEnabled = true, shadowMapSize = 2048 } = config;

	// Design-package match uses pure IBL — zero out the direct lights while keeping the objects for easy re-enable.
	const lightFactor = DESIGN_PACKAGE_LOOK && DESIGN_PACKAGE_NEUTRALIZE_LIGHTS ? 0 : 1;

	// Create ambient light
	const ambientLight = new AmbientLight(preset.ambient.color, preset.ambient.intensity * lightFactor);
	scene.add(ambientLight);
	self.internalAmbientLight = ambientLight;

	// Create directional light
	const directionalLight = new DirectionalLight(preset.directional.color, preset.directional.intensity * lightFactor);
	directionalLight.position.set(
		preset.directional.position.x,
		preset.directional.position.y,
		preset.directional.position.z
	);
	directionalLight.target.position.set(
		preset.directional.target.x,
		preset.directional.target.y,
		preset.directional.target.z
	);

	// Configure shadows
	directionalLight.castShadow = shadowsEnabled;
	directionalLight.shadow.mapSize.width = shadowMapSize;
	directionalLight.shadow.mapSize.height = shadowMapSize;
	directionalLight.shadow.camera.near = 0.5;
	directionalLight.shadow.camera.far = 500;
	directionalLight.shadow.camera.left = -200;
	directionalLight.shadow.camera.right = 200;
	directionalLight.shadow.camera.top = 200;
	directionalLight.shadow.camera.bottom = -200;
	directionalLight.shadow.radius = 2;
	directionalLight.shadow.blurSamples = 16;

	scene.add(directionalLight);
	scene.add(directionalLight.target);
	self.internalDirectionalLight = directionalLight;

	// Store original intensity (kept in sync with lightFactor so highlight restore stays neutralized)
	self.internalDirectionalLightOriginalIntensity = preset.directional.intensity * lightFactor;
	self.internalDirectionalLightOriginalVisible = true;

	// Create hemisphere light
	const hemisphereLight = new HemisphereLight(
		preset.hemisphere.skyColor,
		preset.hemisphere.groundColor,
		preset.hemisphere.intensity * lightFactor
	);
	hemisphereLight.position.set(0, 120, 0);
	scene.add(hemisphereLight);
	self.internalHemisphereLight = hemisphereLight;

	// Note: Highlight spotlight is now handled by the Highlight class in highlight-manager
	// No spotlight is created here - the Highlight class creates spotlights on demand
	self.internalHighlightSpotLight = null;
};
